import React, { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import BillingTestCard from "@app/postBill/components/billingTestCard";
import {
  Bill,
  GetStatementRequestInput,
  Item,
  PaymentPlan,
  StatementStatus,
} from "@app/postBill/models";
import { InformationalActionCard } from "@app/preBill/components/informationalActionCard";
import BillingContactUs from "@app/postBill/components/billingContactUs";
import FinancialAssistanceIcon from "@app/preBill/assets/svg/financial-assistance.svg";
import {
  PostBillAPIContext,
  ConfigContext,
  PostBillContext,
} from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { routes } from "@app/routing";
import { openNewWindow } from "@app/utils";
import { logMsg } from "@app/utils/logMsg";
import {
  getFormattedMonthlyPaymentDate,
  getLimsClinicIds,
  getTestsOrdered,
  orderReadyBills,
  viewPdf,
} from "@app/postBill/utils";
import { PaymentPlanCard } from "@app/postBill/components/paymentPlanCard";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import PaymentSummaryCard from "@app/postBill/components/paymentSummaryCard";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { SpinnerView } from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";

import "./reviewBills.scss";

const messages = defineMessages({
  reviewBillsTitle: {
    id: "reviewBillsTitle",
    defaultMessage: "Review your bills",
  },
  reviewBillsSubtitle: {
    id: "reviewBillsSubtitle",
    defaultMessage: `Select the bills you want to pay, then click "Continue" at the bottom when you're ready.`,
  },
  reviewBillSubtitle: {
    id: "reviewBillSubtitle",
    defaultMessage: `Review your bill, then click "Continue" at the bottom when you're ready.`,
  },
  financialAssistanceTitle: {
    id: "financialAssistanceTitle",
    defaultMessage: "Learn about financial assistance",
  },
  financialAssistanceText: {
    id: "financialAssistanceText",
    defaultMessage:
      "You may qualify to pay as little as $0 — applying takes just a few minutes.",
  },
  reviewBillsContinueButton: {
    id: "reviewBillsContinueButton",
    defaultMessage: "Continue",
  },
  dynamicMonthlyPaymentDate: {
    id: "dynamicMonthlyPaymentDate",
    defaultMessage: "{day} of each month",
  },
});

const MINIMUM_PAYMENT_PLAN_AMOUNT = 500000; // $50.00 in backend (amount / 10000 = dollars)

const ReviewBills: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    apiProviderState: { billingData },
    loadingStates,
    getItems,
    getPaymentPlanPreview,
    getStatementPdf,
    getCTSToken,
  } = useContext(PostBillAPIContext);
  const {
    selectedBills,
    setSelectedBills,
    totalAmount,
    setTotalAmount,
    selectedPaymentPlan,
    setSelectedPaymentPlan,
    selectedPaymentPlanItems,
    setSelectedPaymentPlanItems,
    paidDirectPayments,
    paidPaymentPlans,
  } = useContext(PostBillContext);
  const { config } = useContext(ConfigContext);
  const [bills, setBills] = useState<Bill[]>([]);
  const location = useLocation();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const ctsToken = getCTSToken?.();
  const { paymentPlanId, from } = location.state ?? {};
  const isPaymentPlanDisabled = totalAmount < MINIMUM_PAYMENT_PLAN_AMOUNT;

  const getBillingItems = async () => {
    try {
      await getItems();
    } catch {
      logMsg(
        "error",
        "postbill.review",
        "Billing Review -> Failed to get billing items"
      );
    }
  };

  const handleBack = () => {
    if (from === routes.billingPaymentPlanDetail) {
      navigate(routes.billingPaymentPlanDetail, { state: { paymentPlanId } });
      return;
    }
    navigate(routes.billingCenter);
  };

  const handleFinancialAssistance = () => {
    openNewWindow(config.preBill.contactUsChatbot);
    trackPostBillHeapEvent({ eventKey: HEAP_EVENTS.upp_click_compcare_tile });
  };

  const handleContinueButton = async () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_continuetopay,
      extraData: {
        tests_ordered: getTestsOrdered(billingData?.readyToPay),
        lims_clinic_id: getLimsClinicIds(billingData?.readyToPay),
        pay_plan_length: selectedPaymentPlan,
      },
    });
    navigate(routes.billingPayment);
  };

  const getCheckboxItem = (item: Item) => {
    const index = selectedBills.findIndex(
      (bill: Item) =>
        bill.orderUid === item.orderUid && bill.lineItemUid === item.lineItemUid
    );

    if (index > -1) {
      const tempArray = [...selectedBills];
      tempArray.splice(index, 1);
      setSelectedBills(tempArray);

      setTotalAmount(totalAmount - item.balance);

      if (tempArray.length === 0) {
        setSelectedPaymentPlan(PaymentPlan.None);
      }

      return;
    }

    setSelectedBills([...selectedBills, item]);
    setTotalAmount(totalAmount + item.balance);
  };

  const getFirstPaymentDate = (): string => {
    return intl.formatMessage(messages.dynamicMonthlyPaymentDate, {
      day: getFormattedMonthlyPaymentDate(intl, new Date()),
    });
  };

  const handleShowCheckboxSelected = (bill: Item): boolean => {
    if (selectedBills.length === 0) {
      return true;
    }
    return (
      selectedBills.findIndex(
        (item: Item) =>
          bill.orderUid === item.orderUid &&
          bill.lineItemUid === item.lineItemUid
      ) > -1
    );
  };

  const onViewStatement = async (orderUid: string, lineItemUid: string) => {
    const statementData: GetStatementRequestInput = {
      bosOrderUid: orderUid,
      bosLineItemUid: lineItemUid,
    };
    navigate(routes.billingStatementPdf, {
      state: { statementData, from: routes.reviewBills },
    });
  };

  useEffect(() => {
    const fetchPlanPreview = async () => {
      const patientUid = billingData?.patientUid;

      if (!patientUid) {
        navigate(routes.billingCenter);
        return;
      }

      const hasPaymentPlanSelected = selectedPaymentPlan !== PaymentPlan.None;
      const hasValidBills = selectedBills.length > 0;

      if (
        !hasPaymentPlanSelected ||
        !hasValidBills ||
        billingData?.hasInternationalClinic
      ) {
        return;
      }

      const paymentPlanInput = {
        currency: selectedBills[0]?.currency,
        amount: totalAmount,
        itemList: selectedBills.map((bill) => ({
          bosOrderUid: bill.orderUid,
          bosOrderLineItemUid: bill.lineItemUid,
        })),
        numberOfInstallment: selectedPaymentPlan,
      };

      try {
        const paymentPlanReview = await getPaymentPlanPreview(paymentPlanInput);
        const items = paymentPlanReview.data?.items ?? [];
        setSelectedPaymentPlanItems(items);
      } catch {
        logMsg(
          "error",
          "postbill.review",
          "Error fetching payment plan preview"
        );
      }
    };

    fetchPlanPreview();
  }, [
    selectedPaymentPlan,
    selectedBills,
    billingData?.patientUid,
    totalAmount,
    billingData?.hasInternationalClinic,
  ]);

  useEffect(() => {
    if (ctsToken) {
      getBillingItems();
    }
  }, [ctsToken]);

  useEffect(() => {
    const selectableBills = billingData?.readyToPay.filter(
      (bill: Item) =>
        bill.statementStatus !== StatementStatus.PAYMENT_PENDING &&
        !paidDirectPayments.has(bill.lineItemUid) &&
        !paidPaymentPlans.has(bill.lineItemUid)
    );
    setBills(orderReadyBills(selectableBills as Bill[]));
  }, [billingData?.readyToPay]);

  useEffect(() => {
    if (location.state?.from !== routes.billingPayment) {
      setSelectedPaymentPlan(PaymentPlan.None);
    }
  }, []);

  useEffect(() => {
    if (location.state?.from !== routes.billingPayment) {
      setSelectedBills(bills as Item[]);
      let amount = 0;
      bills.forEach((item) => {
        amount += (item as Item).balance;
      });
      setTotalAmount(amount);
    }
  }, [bills]);

  useEffect(() => {
    if (totalAmount < 50 && selectedPaymentPlan !== PaymentPlan.None) {
      setSelectedPaymentPlan(PaymentPlan.None);
    }
  }, [totalAmount]);

  return (
    <>
      <SpinnerView
        isLoading={
          loadingStates.getPaymentPlanPreview ||
          loadingStates.getStatementPdf ||
          loadingStates.getItems
        }
      />
      <BlueHeaderContent
        blueHeaderTitle={intl.formatMessage(messages.reviewBillsTitle)}
        backArrowHeaderButton={handleBack}
        medium
        noGradient
      >
        <div className="review-bills">
          <div className="review-bills__content">
            <div className="review-bills__subtitle">
              {bills.length === 1
                ? intl.formatMessage(messages.reviewBillSubtitle)
                : intl.formatMessage(messages.reviewBillsSubtitle)}
            </div>
            {!billingData?.hasInternationalClinic && (
              <InformationalActionCard
                icon={FinancialAssistanceIcon}
                title={intl.formatMessage(messages.financialAssistanceTitle)}
                text={intl.formatMessage(messages.financialAssistanceText)}
                onClick={handleFinancialAssistance}
              />
            )}
            <div className="review-bills__bills">
              {bills?.map((bill: Item) => (
                <BillingTestCard
                  key={bill.lineItemUid}
                  testName={bill.testName}
                  amount={bill.balance}
                  caseNumber={bill.caseNumber}
                  dateOfService={bill.dateOfService}
                  dueDate={bill.dueDate}
                  orderedBy={bill.orderedBy}
                  statementStatus={bill.statementStatus}
                  currency={bill.currency}
                  selectable={bills.length > 1}
                  selected={handleShowCheckboxSelected(bill)}
                  onCheckboxClick={() => getCheckboxItem(bill)}
                  hasPdf={bill.hasPdf}
                  onViewStatement={() =>
                    onViewStatement(bill.orderUid, bill.lineItemUid)
                  }
                />
              ))}
            </div>
            {!billingData?.hasInternationalClinic && (
              <PaymentPlanCard
                selectedPlan={selectedPaymentPlan}
                onSelectPlan={setSelectedPaymentPlan}
                onRemovePlan={() => setSelectedPaymentPlan(PaymentPlan.None)}
                disabled={isPaymentPlanDisabled}
              />
            )}
            {selectedBills.length > 0 && (
              <PaymentSummaryCard
                selectedPlan={selectedPaymentPlan}
                testNames={selectedBills.map((bill) => bill.testName.shortName)}
                testPrices={selectedBills.map((bill) => bill.balance)}
                totalPrice={totalAmount}
                currency={selectedBills[0]?.currency}
                firstPaymentDate={getFirstPaymentDate()}
                paymentItems={selectedPaymentPlanItems}
              />
            )}

            <ButtonNoShadow
              raised
              disabled={selectedBills.length === 0}
              onClick={handleContinueButton}
              className="review-bills__continue-button"
              data-testid="billingReviewContinue"
            >
              {intl.formatMessage(messages.reviewBillsContinueButton)}
            </ButtonNoShadow>
            <div className="review-bills__contact-us">
              <BillingContactUs />
            </div>
          </div>
        </div>
      </BlueHeaderContent>
    </>
  );
};

export default ReviewBills;
